import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { verifyOtp, requestOtp } from '../../../services/apiServices';
import { HeaderWithLogo } from '../components/HeaderWithLogo';
import { ScreenHeader } from '../components/ScreenHeader';
import { SubmitButton } from '../components/SubmitButton';
import { BackToLoginButton } from '../components/BackToLoginButton';
import { OTPInput } from '../../../components/common/OTPInput';
import { StatusMessage } from '../../../components/common/StatusMessage';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { useOtpInput, useCountdown } from '../../../hooks';
import { maskEmail } from '../../../utils/stringUtils';
import { formatCountdown } from '../../../utils/timeUtils';
import { getErrorMessage } from '../../../utils/errorHandler';
import {
  SCREENS,
  RESEND_COUNTDOWN_SECONDS,
  MAX_VERIFICATION_ATTEMPTS,
  NAVIGATION_DELAY_MS,
  RESEND_SUCCESS_DISPLAY_MS,
} from '../constants';

/**
 * Verify Code Screen Component
 * Second step in the password reset flow - user enters 6-digit OTP code
 */
const VerifyCodeScreen = ({ email, onNavigate }) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(MAX_VERIFICATION_ATTEMPTS);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const navigate = useNavigate();

  const { code, otp, isComplete, inputRefs, handleInputChange, handleKeyDown, handlePaste } =
    useOtpInput(6);
  const { countdown, resetCountdown, isExpired } = useCountdown(RESEND_COUNTDOWN_SECONDS);

  const handleVerify = async (e) => {
    e.preventDefault();

    if (otp.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    if (attemptsLeft <= 0) {
      setError('Too many failed attempts. Please request a new code');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const data = await verifyOtp({ email, otp });

      if (data?.verified) {
        setSuccess(true);
        setError('');

        setTimeout(() => {
          onNavigate(SCREENS.CHANGE_PASSWORD);
        }, NAVIGATION_DELAY_MS);

        return;
      }

      setAttemptsLeft((prev) => Math.max(prev - 1, 0));
      setError(data?.detail || 'Invalid code');
    } catch (err) {
      setAttemptsLeft((prev) => Math.max(prev - 1, 0));
      setError(getErrorMessage(err) || 'Could not verify the code. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!isExpired) return;

    setIsResending(true);
    setError('');
    setResendSuccess(false);

    try {
      await requestOtp(email);
      setIsResending(false);
      setResendSuccess(true);
      resetCountdown(RESEND_COUNTDOWN_SECONDS);
      setAttemptsLeft(MAX_VERIFICATION_ATTEMPTS);

      setTimeout(() => setResendSuccess(false), RESEND_SUCCESS_DISPLAY_MS);
    } catch (err) {
      setIsResending(false);
      setError(getErrorMessage(err) || 'Could not resend code. Please try again.');
    }
  };

  const handleBackClick = () => {
    onNavigate(SCREENS.FORGOT_PASSWORD);
  };

  const handleBackToLogin = () => {
    navigate('/');
  };

  const canResend = isExpired && !isResending;

  return (
    <>
      <div className="w-full max-w-[512px]">
        <HeaderWithLogo onBackClick={handleBackClick} disabled={isVerifying} />

        <form
          onSubmit={handleVerify}
          className="content-stretch flex flex-col gap-[32px] items-start relative shrink-0 w-full"
        >
          <div className="content-stretch flex flex-col gap-[20px] items-start relative shrink-0 w-full">
            <ScreenHeader
              icon="lock"
              title="Enter verification code"
              description={`We sent a 6-digit code to ${maskEmail(
                email,
              )}. Enter it below to continue.`}
            />
          </div>

          <div className="content-stretch flex flex-col gap-[32px] items-center relative shrink-0 w-full">
            <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full">
              <p
                className="font-['Roboto:Regular',sans-serif] font-normal leading-[normal] relative shrink-0 text-[14px] text-black w-full"
                style={{ fontVariationSettings: "'wdth' 100" }}
              >
                Verification Code
              </p>

              <OTPInput
                code={code}
                inputRefs={inputRefs}
                onInputChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                error={error}
                disabled={isVerifying || success}
              />

              <StatusMessage type="error" message={error} className="mt-2" />
              <StatusMessage
                type="success"
                message={success ? 'Code verified. You can reset your password now.' : ''}
                className="mt-2"
              />
              <StatusMessage
                type="success"
                message={resendSuccess ? 'New code sent' : ''}
                className="mt-2"
              />
            </div>

            <SubmitButton
              onClick={handleVerify}
              disabled={!isComplete || success}
              isLoading={isVerifying}
              loadingText="Verifying..."
            >
              Verify Code
            </SubmitButton>

            {/* Resend Section */}
            <div className="flex flex-col gap-2 items-center w-full">
              <button
                type="button"
                onClick={handleResend}
                disabled={!canResend}
                className={`font-['Roboto:Regular',sans-serif] font-normal text-[16px] bg-transparent border-none cursor-pointer p-0 ${
                  canResend
                    ? 'text-[#61b8e7] underline hover:text-[#4fa5d5]'
                    : 'text-[#c3c3c5] no-underline cursor-not-allowed'
                }`}
                style={{ fontVariationSettings: "'wdth' 100" }}
              >
                {isResending ? (
                  <span className="flex items-center gap-2">
                    <LoadingSpinner size="h-4 w-4" />
                    Sending...
                  </span>
                ) : (
                  'Resend code'
                )}
              </button>

              {!isExpired && (
                <p
                  className="font-['Roboto:Regular',sans-serif] font-normal text-[14px] text-[#6e6e71]"
                  style={{ fontVariationSettings: "'wdth' 100" }}
                >
                  You can resend in {formatCountdown(countdown)}
                </p>
              )}

              <BackToLoginButton onClick={handleBackToLogin} disabled={isVerifying} />
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default VerifyCodeScreen;
